(() => {
  if (customElements.get('fx-product-promise')) return;

  class FxProductPromise extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.tabs = Array.from(this.querySelectorAll('[data-promise-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-promise-panel]'));
      this.dots = Array.from(this.querySelectorAll('[data-promise-dot]'));
      this.stage = this.querySelector('[data-promise-stage]');
      this.tabsRail = this.querySelector('.fx-promise-cards');
      this.status = this.querySelector('[data-promise-status]');
      if (!this.tabs.length || this.tabs.length !== this.panels.length) return;

      this.activeIndex = Math.max(0, this.tabs.findIndex((tab) => tab.classList.contains('is-active')));
      this.touchStart = null;
      this.preloadTimer = null;
      this.scrollTimer = null;
      this.scrollFrame = null;
      this.abortController = new AbortController();
      this.mobileQuery = window.matchMedia('(max-width: 989px)');
      this.desktopQuery = window.matchMedia('(min-width: 990px)');
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.queueScrollActivation = () => {
        if (!this.desktopQuery.matches || this.scrollFrame) return;
        this.scrollFrame = window.requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.activateNearestScrolledTab();
        });
      };

      this.bindEvents();
      this.setActive(this.activeIndex);
      this.queueScrollActivation();
    }

    disconnectedCallback() {
      window.clearTimeout(this.preloadTimer);
      window.clearTimeout(this.scrollTimer);
      window.cancelAnimationFrame(this.scrollFrame);
      this.abortController?.abort();
      delete this.dataset.initialized;
    }

    bindEvents() {
      const signal = this.abortController.signal;

      this.addEventListener('click', (event) => {
        const tab = event.target.closest('[data-promise-tab]');
        if (tab) {
          this.setActive(Number.parseInt(tab.dataset.promiseIndex, 10), true);
          return;
        }

        const dot = event.target.closest('[data-promise-dot]');
        if (dot) {
          this.setActive(Number.parseInt(dot.dataset.promiseIndex, 10), true);
          return;
        }

        if (event.target.closest('[data-promise-previous]')) {
          this.step(-1);
          return;
        }

        if (event.target.closest('[data-promise-next]')) this.step(1);
      }, { signal });

      this.addEventListener('keydown', (event) => {
        const tab = event.target.closest('[data-promise-tab]');
        if (!tab) return;

        const currentIndex = Number.parseInt(tab.dataset.promiseIndex, 10);
        let nextIndex = null;
        if (event.key === 'ArrowDown' || event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.tabs.length;
        if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = this.tabs.length - 1;
        if (nextIndex === null) return;

        event.preventDefault();
        this.tabs[nextIndex].focus();
        this.setActive(nextIndex, true);
      }, { signal });

      this.tabsRail?.addEventListener('scroll', () => {
        if (!this.mobileQuery.matches) return;
        window.clearTimeout(this.scrollTimer);
        this.scrollTimer = window.setTimeout(() => this.activateNearestTab(), 90);
      }, { passive: true, signal });

      window.addEventListener('scroll', this.queueScrollActivation, { passive: true, signal });
      window.addEventListener('resize', this.queueScrollActivation, { passive: true, signal });

      this.stage?.addEventListener('touchstart', (event) => {
        if (event.touches.length !== 1) return;
        const touch = event.touches[0];
        this.touchStart = { x: touch.clientX, y: touch.clientY };
      }, { passive: true, signal });

      this.stage?.addEventListener('touchend', (event) => {
        if (!this.touchStart || event.changedTouches.length !== 1) return;
        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - this.touchStart.x;
        const deltaY = touch.clientY - this.touchStart.y;
        this.touchStart = null;

        if (Math.abs(deltaX) < 44 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.15) return;
        const direction = deltaX < 0 ? 1 : -1;
        this.setActive((this.activeIndex + direction + this.tabs.length) % this.tabs.length, true);
      }, { passive: true, signal });

      this.addEventListener('shopify:block:select', (event) => {
        const tab = event.target.closest('[data-promise-tab]');
        const index = this.tabs.indexOf(tab);
        if (index >= 0) this.setActive(index, true);
      }, { signal });
    }

    activateNearestScrolledTab() {
      if (!this.desktopQuery.matches) return;

      const storyRect = this.getBoundingClientRect();
      if (storyRect.bottom <= 0 || storyRect.top >= window.innerHeight) return;

      const activationLine = window.innerHeight * 0.48;
      let nearestIndex = this.activeIndex;
      let nearestDistance = Number.POSITIVE_INFINITY;

      this.tabs.forEach((tab, index) => {
        const rect = tab.getBoundingClientRect();
        const distance = Math.abs(rect.top + (rect.height / 2) - activationLine);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      if (nearestIndex !== this.activeIndex) this.setActive(nearestIndex, false);
    }

    activateNearestTab() {
      if (!this.tabsRail || !this.mobileQuery.matches) return;

      const railCenter = this.tabsRail.scrollLeft + (this.tabsRail.clientWidth / 2);
      let nearestIndex = this.activeIndex;
      let nearestDistance = Number.POSITIVE_INFINITY;

      this.tabs.forEach((tab, index) => {
        const tabCenter = tab.offsetLeft + (tab.offsetWidth / 2);
        const distance = Math.abs(tabCenter - railCenter);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      if (nearestIndex !== this.activeIndex) this.setActive(nearestIndex, false);
    }

    step(direction) {
      const nextIndex = (this.activeIndex + direction + this.tabs.length) % this.tabs.length;
      this.setActive(nextIndex, true);
    }

    setActive(requestedIndex, alignTab = false) {
      if (!Number.isFinite(requestedIndex) || !this.tabs[requestedIndex]) return;
      this.activeIndex = requestedIndex;

      this.tabs.forEach((tab, index) => {
        const active = index === requestedIndex;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });

      this.panels.forEach((panel, index) => {
        const active = index === requestedIndex;
        panel.classList.toggle('is-active', active);
        panel.setAttribute('aria-hidden', String(!active));
      });

      this.dots.forEach((dot, index) => {
        const active = index === requestedIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', String(active));
      });

      if (this.status) {
        const current = String(requestedIndex + 1).padStart(2, '0');
        const total = String(this.tabs.length).padStart(2, '0');
        this.status.textContent = `${current} / ${total}`;
      }

      this.loadPanel(this.panels[requestedIndex]);
      this.schedulePreload(this.panels[(requestedIndex + 1) % this.panels.length]);

      if (alignTab && this.mobileQuery.matches) {
        this.tabs[requestedIndex].scrollIntoView({
          behavior: this.reducedMotionQuery.matches ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }

      if (alignTab && this.desktopQuery.matches) {
        this.tabs[requestedIndex].scrollIntoView({
          behavior: this.reducedMotionQuery.matches ? 'auto' : 'smooth',
          block: 'center',
          inline: 'nearest',
        });
      }
    }

    loadPanel(panel) {
      if (!panel) return;

      panel.querySelectorAll('source[data-srcset]').forEach((source) => {
        source.srcset = source.dataset.srcset;
        delete source.dataset.srcset;
      });

      const image = panel.querySelector('img[data-src], img[data-srcset], img[src]');
      if (!image) return;

      if (image.dataset.srcset) {
        image.srcset = image.dataset.srcset;
        delete image.dataset.srcset;
      }

      if (image.dataset.src) {
        image.src = image.dataset.src;
        delete image.dataset.src;
      }
    }

    schedulePreload(panel) {
      window.clearTimeout(this.preloadTimer);
      if (!panel) return;
      this.preloadTimer = window.setTimeout(() => this.loadPanel(panel), 900);
    }
  }

  customElements.define('fx-product-promise', FxProductPromise);
})();
